import React, { useState, useEffect } from 'react';
import range from 'lodash/range';
import find from 'lodash/find';

import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import ImageList from '@material-ui/core/ImageList';
import ImageListItem from '@material-ui/core/ImageListItem';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import SvgIcon from '@material-ui/core/SvgIcon';
import { ReactComponent as StampIcon } from '../../assets/icons/Stamp.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/Check.svg';

import * as StampCampaignService from '../../api/stampCampaignService';

import Paper from '../Paper';

const useStampStyles = makeStyles((theme) => ({
  root: ({ highlight }: { highlight: boolean }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: highlight ? 2 : 1,
    borderStyle: 'solid',
    borderColor: highlight
      ? theme.palette.primary.main
      : theme.palette.grey[300],
    width: 40,
    height: 40,
    borderRadius: 40 / 2,
  }),
  icon: {
    color: theme.palette.primary.main,
  },
  fillRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
    width: 40,
    height: 40,
    borderRadius: 40 / 2,
    backgroundColor: theme.palette.primary.main,
  },
}));

export function Stamp({
  value,
  highlight,
  currentStamp,
}: {
  value: number;
  highlight: boolean;
  currentStamp: number;
}) {
  const classes = useStampStyles({ highlight });

  return (
    <>
      {value <= currentStamp ? (
        <div className={classes.fillRoot}>
          <SvgIcon
            component={CheckIcon}
            color="primary"
            className={classes.icon}
            viewBox={'0 0 30 30'}
          />
        </div>
      ) : (
        <div className={classes.root}>
          {highlight ? (
            <SvgIcon
              component={StampIcon}
              color="primary"
              className={classes.icon}
            />
          ) : (
            <Typography variant="subtitle2">{value}</Typography>
          )}
        </div>
      )}
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 15,
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(3),
    width: 350,
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'center',
  },
  avatar: {
    height: 80,
    width: 80,
    boxShadow: '0 2px 10px rgba(199, 199, 199, 0.5)',
    marginBottom: theme.spacing(2),
  },
  stampCardRoot: {
    [theme.breakpoints.up('sm')]: {
      padding: `${theme.spacing(2.5)}px ${theme.spacing(3)}px`,
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(),
    },
  },
  tile: {
    display: 'flex',
    justifyContent: 'center',
  },
  milestonesContainer: {
    marginTop: theme.spacing(),
  },
  minSpend: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  subtext: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    lineHeight: '12px',
  },
  subtextPrize: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    lineHeight: '12px',
  },
  minSpendPrize: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  descContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  descText: {
    fontWeight: theme.typography.fontWeightRegular,
    textAlign: 'center',
  },
  subtextContainerPrize: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  subtextContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  markdown: {
    fontFamily: 'ClarikaGeometric',
    fontSize: 14,
    fontWeight: 400,
  },
  buttonContainer: {
    marginTop: theme.spacing(),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  removeStamp: {
    marginRight: theme.spacing(2),
    width: 150,
  },
  addStamp: {
    width: 150,
  },
  reissuedText: {
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.grey[500],
  },
  deleteCardContainer: {
    marginLeft: 'auto',
  },
  deleteIcon: {
    color: theme.palette.text.secondary,
    width: '20px',
    height: '20px',
  },
  cardName: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
}));

type Props = {
  cardName: string;
  merchantId: string;
  maxStamps: number;
  quantity: number;
  handleAddStamp: () => void;
  handleRemoveStamp: () => void;
  stampCardStatus: string;
  isAdminOnly: boolean;
  campaignType: string;
  isMultipleCardAllowed: boolean;
  handleDeleteCard: () => void;
  handleReissueCard: () => void;
  stampCampaignId: string;
  companyId: string;
  stampCampaignList: StampCampaignService.StampCampaign[];
};

export default function StampCard({
  cardName,
  maxStamps,
  quantity,
  handleAddStamp,
  handleRemoveStamp,
  stampCardStatus,
  isAdminOnly,
  campaignType,
  isMultipleCardAllowed,
  handleDeleteCard,
  handleReissueCard,
  stampCampaignId,
  companyId,
  stampCampaignList,
}: Props) {
  const classes = useStyles();
  const [highlightedKeys, setHighlightedKeys] = useState<number[]>([]);
  const updatedMaxStamps =
    stampCardStatus === 'completed' ? quantity : maxStamps;
  const isStampCampaignExist = find(stampCampaignList, function (campaign) {
    return campaign.id === stampCampaignId;
  });

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await StampCampaignService.listMilestones(
          companyId,
          stampCampaignId
        );
        if (res.length > 0) {
          let highlight = res.map((m) => m.threshold);
          setHighlightedKeys(highlight);
        }
      } catch (e) {
        console.log('Error:', e);
      }
    };
    fetch();
  }, [companyId, stampCampaignId]);

  return (
    <Grid item xs={12} className={classes.root} key={stampCampaignId}>
      <Paper className={classes.cardContainer}>
        <Typography variant="h5" className={classes.cardName}>
          {cardName}
        </Typography>
        <div className={classes.stampCardRoot}>
          <ImageList
            rowHeight={40}
            cols={updatedMaxStamps < 5 ? updatedMaxStamps : 5}
            gap={16}
            className={classes.tile}
          >
            {range(1, updatedMaxStamps + 1).map((x) => {
              return (
                <ImageListItem
                  key={x}
                  cols={1}
                  rows={1}
                  className={classes.tile}
                >
                  <Stamp
                    key={x}
                    value={x}
                    highlight={highlightedKeys.includes(x) && x > quantity}
                    currentStamp={quantity}
                  />
                </ImageListItem>
              );
            })}
          </ImageList>
        </div>
        <div className={classes.buttonContainer}>
          {isAdminOnly && quantity > 0 && stampCardStatus !== 'completed' && (
            <Button
              className={classes.removeStamp}
              onClick={(e) => {
                handleRemoveStamp();
              }}
            >
              Remove Stamps
            </Button>
          )}
          {stampCardStatus === 'completed' && !isAdminOnly && (
            <Typography
              variant="body2"
              className={classes.reissuedText}
              gutterBottom
            >
              <strong>Card may be reissued by Manager or Admin</strong>
            </Typography>
          )}
          {stampCardStatus === 'active' && (
            <Button
              color="primary"
              className={classes.addStamp}
              onClick={(e) => {
                handleAddStamp();
              }}
            >
              Add Stamps
            </Button>
          )}
          {isAdminOnly &&
            stampCardStatus === 'completed' &&
            isStampCampaignExist && (
              <Button
                color="primary"
                className={classes.addStamp}
                onClick={(e) => {
                  handleReissueCard();
                }}
              >
                Reissue Card
              </Button>
            )}
        </div>
        <div className={classes.deleteCardContainer}>
          <IconButton onClick={handleDeleteCard}>
            <DeleteIcon className={classes.deleteIcon} />
          </IconButton>
        </div>
      </Paper>
    </Grid>
  );
}
